import React from 'react';

import { useIntl } from 'react-intl-next';

import {
	PanelSplitter,
	SideNav,
	SideNavContent,
	SideNavFooter,
	SideNavHeader,
	TopLevelSpacer,
} from '@atlaskit/navigation-system';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { SideNavPanelSplitter } from '@atlaskit/navigation-system/layout/side-nav';
import { fg } from '@atlaskit/platform-feature-flags';

import { AsyncIntlProvider } from '../../common/utils/locale';

import { messages } from './messages';
import type { SideNavigationLayoutProps } from './types';

/**
 * SideNavigationLayout - A layout component that provides a side navigation structure.
 *
 * This component is designed to be used as a wrapper for the side navigation system,
 * providing a consistent layout with header, content, and footer sections.
 * This is a controlled and will not manage any state internally.
 *
 * If you need state management, analytics, or supporting Drag and Drop
 * please consider using the `GlobalSideNavigation` component instead.
 *
 * Props:
 * - preMainElem: Element to render before the main content.
 * - postMainElem: Element to render after the main content.
 * - bottomElem: Element to render at the bottom of the side navigation.
 * - children: Main content of the side navigation layout.
 * - testId: Optional test identifier for testing purposes.
 * - onExpand: Callback when the side nav is expanded.
 * - onCollapse: Callback when the side nav is collapsed.
 * - onResizeStart: Callback when the resize of the side nav starts.
 * - onResizeEnd: Callback when the resize of the side nav ends.
 * - defaultWidth: Default width of the side nav.
 * - defaultCollapsed: Whether the side nav is collapsed by default.
 * - sideNavContentRef: Ref for the SideNavContent component.
 *
 */
export function SideNavigationLayout(props: SideNavigationLayoutProps): React.JSX.Element {
	return (
		<AsyncIntlProvider>
			<BaseLayout {...props} />
		</AsyncIntlProvider>
	);
}

function PanelSplitterWithFG(props: React.ComponentPropsWithoutRef<typeof PanelSplitter>) {
	const isFhsEnabled = fg('navx-2566-implement-fhs-rollout')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFhsEnabled()
		: fg('navx-full-height-sidebar');

	const { formatMessage } = useIntl();

	if (isFhsEnabled) {
		return (
			<SideNavPanelSplitter
				{...props}
				tooltipContent={formatMessage(messages.panelSplitterTooltipContent)}
			/>
		);
	}

	return <PanelSplitter {...props} />;
}

function BaseLayout({
	preMainElem,
	postMainElem,
	bottomElem,
	children,
	testId,
	onExpand,
	onCollapse,
	onResizeStart,
	onResizeEnd,
	defaultWidth,
	defaultCollapsed,
	sideNavContentRef,
}: SideNavigationLayoutProps) {
	const { formatMessage } = useIntl();

	return (
		<SideNav
			testId={testId}
			onExpand={onExpand}
			onCollapse={onCollapse}
			defaultWidth={defaultWidth}
			defaultCollapsed={defaultCollapsed}
		>
			{preMainElem && <SideNavHeader>{preMainElem}</SideNavHeader>}
			<SideNavContent ref={sideNavContentRef}>
				{children}
				<TopLevelSpacer />
				{postMainElem}
			</SideNavContent>
			<PanelSplitterWithFG
				onResizeEnd={onResizeEnd}
				onResizeStart={onResizeStart}
				label={formatMessage(messages.panelSplitterLabel)}
			/>
			{bottomElem && <SideNavFooter>{bottomElem}</SideNavFooter>}
		</SideNav>
	);
}
